import React, { useCallback, useReducer, useRef } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import styled from 'styled-components';
import { RhfCheckbox } from '@packages/ui/hook-form-adapters';
import { Typography } from '@packages/ui/primitives';
import { ScrollArea } from '@packages/ui/composites';
import { COLORS } from '@packages/ui/tokens';
import type { FormScrollAgreementNode } from '../types/layout';
import { useFormEngineForm } from '../context/FormEngineContext';

const SCROLL_THRESHOLD_PX = 8;

const AgreementFrame = styled.div`
  border: 1px solid ${COLORS.Gray100};
  border-radius: 8px;
  overflow: hidden;
`;

const AgreementContent = styled(Typography)`
  white-space: pre-wrap;
  padding: 16px;
`;

interface ScrollState {
  hasScrolledToBottom: boolean;
}

type ScrollAction = { type: 'REACHED_BOTTOM' };

function scrollReducer(_state: ScrollState, action: ScrollAction): ScrollState {
  switch (action.type) {
    case 'REACHED_BOTTOM':
      return { hasScrolledToBottom: true };
  }
}

export interface FormScrollAgreementRendererProps {
  content: string;
  /** When provided, renders an "I agree" checkbox bound to this field path. */
  name?: string;
  control?: Control<FieldValues>;
  namePrefix?: string;
}

export const FormScrollAgreementRenderer = React.memo(function FormScrollAgreementRenderer({
  content,
  name,
  control: controlProp,
  namePrefix,
}: FormScrollAgreementRendererProps): React.ReactElement {
  const { form } = useFormEngineForm();
  const control = controlProp ?? (form.control as unknown as Control<FieldValues>);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [scrollState, dispatchScroll] = useReducer(scrollReducer, { hasScrolledToBottom: false });

  const fieldName = name
    ? (namePrefix ? `${namePrefix}.${name}` : name)
    : undefined;

  const handleScroll = useCallback(() => {
    if (scrollState.hasScrolledToBottom) return;
    const el = viewportRef.current;
    if (!el) return;
    const atBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_THRESHOLD_PX;
    if (atBottom) {
      dispatchScroll({ type: 'REACHED_BOTTOM' });
    }
  }, [scrollState.hasScrolledToBottom]);

  return (
    <AgreementFrame data-form-scroll-agreement={fieldName ?? 'standalone'}>
      <ScrollArea.Root style={{ height: '240px' }} semanticId={fieldName ?? 'scrollAgreement'}>
        <ScrollArea.Viewport ref={viewportRef} onScroll={handleScroll}>
          <AgreementContent variant='body-s'>{content}</AgreementContent>
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar orientation='vertical'>
          <ScrollArea.Thumb />
        </ScrollArea.Scrollbar>
      </ScrollArea.Root>

      {fieldName ? (
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${COLORS.Gray100}` }}>
          <RhfCheckbox
            control={control}
            name={fieldName}
            label='I agree'
            disabled={!scrollState.hasScrolledToBottom}
            semanticId={fieldName}
          />
        </div>
      ) : null}
    </AgreementFrame>
  );
});

interface FormScrollAgreementNodeRendererProps {
  node: FormScrollAgreementNode;
  namePrefix?: string;
}

export const FormScrollAgreementNodeRenderer = React.memo(function FormScrollAgreementNodeRenderer({
  node,
  namePrefix,
}: FormScrollAgreementNodeRendererProps): React.ReactElement {
  return (
    <FormScrollAgreementRenderer
      content={node.content}
      name={node.name}
      namePrefix={namePrefix}
    />
  );
});
